import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useCart } from "../../contexts/CartContext";
import logoPng from "../../assets/images/logo.png";
import http from "../../lib/http";
import "../../styles/Header.css";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/explore", label: "Explore" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const USER_MENU_ITEMS = [
  {
    path: "/account",
    label: "My Account",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="7" r="4" />
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      </svg>
    ),
  },
  {
    path: "/orders",
    label: "My Orders",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" />
        <line x1="3" y1="6" x2="21" y2="6" />
        <path d="M16 10a4 4 0 0 1-8 0" />
      </svg>
    ),
  },
  {
    path: "/account/wishlist",
    label: "My Wishlist",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
      </svg>
    ),
  },
  {
    path: "/account/reviews",
    label: "My Reviews",
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
  },
];

const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const CartIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="7" r="4" />
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
  </svg>
);

const LogoutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ArrowUpRightIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <path d="M7 17l9.2-9.2M17 17V7H7" />
  </svg>
);

export default function HeaderAuth() {
  const { user, logout } = useAuth();
  const { cartCount, fetchCartCount } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const userMenuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (user) fetchCartCount();
  }, [user, location.pathname, fetchCartCount]);

  useEffect(() => {
    const keyword = query.trim();

    const timer = setTimeout(async () => {
      if (keyword.length < 2) {
        setResults([]);
        setSearchLoading(false);
        return;
      }

      try {
        setSearchLoading(true);

        const { data } = await http.get("/api/customer/products", {
          params: {
            search: keyword,
            per_page: 7,
          },
        });

        setResults(data?.data || []);
      } catch (error) {
        console.error("Search failed:", error);
        setResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }

      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setQuery("");
        setResults([]);
        setSearchLoading(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setMobileOpen(false);

    logout();
    navigate("/");

    try {
      await http.post("/api/customer/logout");
    } catch {
      // ignore
    }
  };

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/" || location.pathname === "/home"
      : location.pathname === path;

  const handleProductClick = (slug) => {
    if (!slug) return;

    navigate(`/product/${slug}`);
    setQuery("");
    setResults([]);
    setSearchLoading(false);
    setMobileOpen(false);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Escape") {
      setQuery("");
      setResults([]);
      setSearchLoading(false);
      return;
    }

    if (e.key === "Enter" && query.trim()) {
      navigate(`/explore?search=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setResults([]);
      setSearchLoading(false);
    }
  };

  return (
    <>
      <header className={`hdr-root ${scrolled ? "hdr-scrolled" : ""}`}>
        <div className="hdr-container">
          <Link to="/" className="hdr-logo" onClick={() => setMobileOpen(false)}>
            <img src={logoPng} alt="Easy World Online" className="hdr-logo-img" />
          </Link>

          <nav className="hdr-nav">
            {NAV_LINKS.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={`hdr-nav-link ${isActive(to) ? "active" : ""}`}
              >
                {label}
                <span className="hdr-nav-link-bar" />
              </Link>
            ))}
          </nav>

          <div className="hdr-actions">
            <div className="hdr-search-wrap" ref={searchRef}>
              <div className={`hdr-search ${query ? "focused" : ""}`}>
                <span className="hdr-search-icon">
                  <SearchIcon />
                </span>

                <input
                  className="hdr-search-input"
                  type="text"
                  placeholder="Search products…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />

                {query && (
                  <button
                    type="button"
                    className="hdr-search-clear"
                    onClick={() => {
                      setQuery("");
                      setResults([]);
                      setSearchLoading(false);
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>

              {query.trim().length >= 2 && (
                <div className="hdr-search-dropdown">
                  {searchLoading ? (
                    <div className="hdr-search-empty">Searching...</div>
                  ) : results.length > 0 ? (
                    results.slice(0, 7).map((p, i) => (
                      <button
                        key={p.id || i}
                        type="button"
                        className="hdr-search-item"
                        onClick={() => handleProductClick(p.slug || p.id)}
                      >
                        <span className="hdr-search-item-icon">
                          <SearchIcon />
                        </span>

                        <span className="hdr-search-item-name">
                          {p.name || p.title || "Product"}
                        </span>

                        <span className="hdr-search-item-arrow">
                          <ArrowUpRightIcon />
                        </span>
                      </button>
                    ))
                  ) : (
                    <div className="hdr-search-empty">No products found</div>
                  )}
                </div>
              )}
            </div>

            <button
              type="button"
              className="hdr-icon-btn"
              onClick={() => navigate("/cart")}
              title="Cart"
            >
              <CartIcon />
              {cartCount > 0 && (
                <span className="hdr-cart-badge">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </button>

            <div className="hdr-user-wrap" ref={userMenuRef}>
              <button
                type="button"
                className={`hdr-icon-btn hdr-user-btn ${userMenuOpen ? "active" : ""}`}
                onClick={() => setUserMenuOpen((v) => !v)}
                title="Account"
              >
                <UserIcon />
                {user?.name && (
                  <span className="hdr-user-name">
                    {user.name.split(" ")[0]}
                  </span>
                )}
              </button>

              {userMenuOpen && (
                <div className="hdr-user-dropdown">
                  <div className="hdr-dropdown-header">
                    <div className="hdr-dropdown-avatar">
                      {user?.name ? user.name[0].toUpperCase() : "U"}
                    </div>

                    <div className="hdr-dropdown-user-info">
                      <div className="hdr-dropdown-user-name">
                        {user?.name || "User"}
                      </div>
                      <div className="hdr-dropdown-user-email">
                        {user?.email}
                      </div>
                    </div>
                  </div>

                  <div className="hdr-dropdown-divider" />

                  {USER_MENU_ITEMS.map(({ path, label, icon }) => (
                    <button
                      key={path}
                      type="button"
                      className="hdr-dropdown-item"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate(path);
                      }}
                    >
                      <span className="hdr-dropdown-item-icon">{icon}</span>
                      <span className="hdr-dropdown-item-label">{label}</span>
                    </button>
                  ))}

                  <div className="hdr-dropdown-divider" />

                  <button
                    type="button"
                    className="hdr-dropdown-item hdr-dropdown-logout"
                    onClick={handleLogout}
                  >
                    <span className="hdr-dropdown-item-icon">
                      <LogoutIcon />
                    </span>
                    <span className="hdr-dropdown-item-label">Logout</span>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              className="hdr-mobile-toggle"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              {mobileOpen ? <CloseIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div className="hdr-mobile-overlay" onClick={() => setMobileOpen(false)}>
          <div className="hdr-mobile-panel" onClick={(e) => e.stopPropagation()}>
            <div className="hdr-mobile-user">
              <div className="hdr-dropdown-avatar hdr-mobile-avatar">
                {user?.name ? user.name[0].toUpperCase() : "U"}
              </div>

              <div>
                <div className="hdr-mobile-user-name">
                  {user?.name || "Welcome"}
                </div>
                <div className="hdr-mobile-user-email">{user?.email}</div>
              </div>
            </div>

            <div className="hdr-mobile-divider" />

            <nav className="hdr-mobile-nav">
              {NAV_LINKS.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`hdr-mobile-link ${isActive(to) ? "active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </nav>

            <div className="hdr-mobile-divider" />

            <div className="hdr-mobile-account">
              {USER_MENU_ITEMS.map(({ path, label, icon }) => (
                <button
                  key={path}
                  type="button"
                  className="hdr-mobile-action"
                  onClick={() => {
                    setMobileOpen(false);
                    navigate(path);
                  }}
                >
                  <span className="hdr-mobile-action-icon">{icon}</span>
                  {label}
                </button>
              ))}
            </div>

            <div className="hdr-mobile-divider" />

            <button
              type="button"
              className="hdr-mobile-logout"
              onClick={handleLogout}
            >
              <LogoutIcon /> Logout
            </button>
          </div>
        </div>
      )}
    </>
  );
}